import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { motion } from 'motion/react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  HelpCircle,
  ChevronDown,
  ChevronRight,
  XCircle,
  Loader2,
} from 'lucide-react';

// ─── FAQ content and plan for adding more ─────────────────────────────────
// Answer style: Start with where to go (e.g. "Go to X from the sidebar"), then the exact
// button name in quotes ("Click X"), describe what to provide or do in plain language, and
// end with the outcome. Add "You can..." for optional next steps. Keep sentences clear;
// use exact UI labels (button names) so users can follow step-by-step.
// Bold: use <b>...</b> in the answer string (e.g. <b>"Invite in Bulk"</b>); answers are
// rendered with dangerouslySetInnerHTML so HTML is applied.
// Each item can include tag: 'technical' | 'account' | 'billing' | 'other'.
// • Add more: append { question, answer, tag } to FAQ_ITEMS.
// • Later: move to GET /api/help/faqs for non-developer edits.
const FAQ_ITEMS = [
  {
    question: 'How do I create a job posting?',
    answer:
      'Go to Job Posts from the sidebar, click <b>"Create Job"</b> → provide a detailed description of the candidate you want including required skills, experience, location and other requirements and let our highly trained AI create a job posting. You can create multiple Screening rounds inside a Job Post as per your requirements.',
    tag: 'technical',
  },
  {
    question: 'How do I add candidates to a job? What are the sourcing types?',
    answer:
      'Go to Job Posts from the sidebar → Open your job from the Job Posts list, choose your Screening → Click <b>"Invite"</b> button. You get three options → You can either copy the link and paste it in your job post or you can invite a single candidate by entering their email address. You can also invite a bulk of candidates by uploading a CSV or XLSX file or ZIP/RAR archive of resumes (supports PDF, DOCX, DOC; max 50MB, up to 500 candidates).',
    tag: 'technical',
  },
  {
    question: 'What is the flow for Bulk Import Candidates?',
    answer:
      'Go to Job Posts from the sidebar → Open your job from the Job Posts list, choose your Screening → Click "Invite" button. You get three options → Click <b>"Invite in Bulk"</b> you can provide a CSV or XLSX file or ZIP/RAR archive of resumes (supports PDF, DOCX, DOC; max 50MB, up to 500 candidates). We will create candidates and email them a <b>specialized screening link</b> where they can apply with resumes and complete the assessment + AI interview. Their results appear automatically on the Candidates page.',
    tag: 'technical',
  },
  {
    question: 'What is the flow for adding a single candidate?',
    answer:
      'Go to Job Posts from the sidebar → Open your job from the Job Posts list, choose your Screening → Click <b>"Invite"</b> button. You get three options → Click <b>"Single Candidate"</b> you can provide a Name and Email, we will create a candidate and email them <b>specialized screening link</b> where candidates can apply with resumes and give Assessment and AI Interview, we will capture their results and you can see the Candidates page.',
    tag: 'technical',
  },
  {
    question: 'How do I use the external Job Link so candidates can apply?',
    answer:
      'Go to Job Posts from the sidebar → Open your job from the Job Posts list, choose your Screening → Click <b>"Invite"</b>. You get three options → Click <b>"Job Link"</b> to generate a public apply link and share it on job boards, WhatsApp, LinkedIn or Naukri. Candidates who open the link can apply with resumes and complete Assessment and AI Interview; we capture their results and you can see them on the Candidates page.',
    tag: 'technical',
  },
  {
    question: 'Can I bulk email selected candidates?',
    answer:
      'Yes. Go to Job Posts → open your job → open the Screening → go to the <b>Selected</b> tab. In the Selected Candidates section, select one or more selected candidates using the checkboxes, then click <b>"Next Steps"</b>. In the modal, choose <b>"Send Email to Candidate"</b>. The email modal opens with the selected candidates; enter or edit the subject and body, configure follow-up if needed, then click <b>"Send Email"</b>. Emails are sent to all selected candidates.',
    tag: 'technical',
  },
  {
    question: 'Can I use a previous draft email when I invite candidates to a screening?',
    answer:
      'Yes. When you first invite candidates (Single candidate or Invite in bulk) and reach the email step, you configure the invitation email and follow-up. Once you send, the platform saves this as the invite draft for that screening. The next time you invite more candidates to the same screening, the previous draft is loaded automatically—subject, body, and follow-up settings appear pre-filled in the email modal. You can edit and send or leave as is.',
    tag: 'technical',
  },
  {
    question: 'How do I configure the invitation email and follow-up?',
    answer:
      'When you invite candidates (Single candidate or Invite in bulk), after you add them you click <b>"Configure & Invite"</b>. The Send Email to Candidates modal opens. On the first step, set the subject and edit the email body. Click <b>"Next"</b> to go to the follow-up step. There you can turn on follow-up (or check <b>"Don\'t need follow-up"</b> to skip), set how many follow-ups (1 or 2), the frequency (e.g. In 2 Days), and the follow-up subject. Click <b>"Preview email"</b> then <b>"Send Email"</b> to send. Your choices are saved as the screening’s invite draft for next time.',
    tag: 'technical',
  },
  {
    question: 'A candidate had an error while applying or during Assessment or AI Interview. Can I resend the invitation link?',
    answer:
      'Yes. Go to the Screening → <b>Candidates</b> tab. Find the candidate. You can either open the row actions (three dots) and click <b>"Resend invitation"</b>, or select one or more candidates and click the amber <b>"Resend"</b> button above the table. A confirmation modal appears: <b>"Resend invitation and clear data?"</b> It explains that a new link will be sent and the candidate’s existing assessment and AI interview data will be cleared so they can start fresh. Click <b>"Resend invitation"</b> in the modal to confirm. The candidate receives the new link by email.',
    tag: 'technical',
  },
  {
    question: 'Where can I update my name, company, or position?',
    answer:
      'Go to <b>Your Account</b> from the sidebar. Open the <b>Account</b> tab. Click <b>Edit profile</b> (top right). Edit Name, Company, and Position in the table, then click <b>Save</b> to apply or <b>Cancel</b> to discard. Your email is shown but cannot be edited from this screen.',
    tag: 'account',
  },
  {
    question: 'Where do I find my account details and email?',
    answer:
      'Go to <b>Your Account</b> from the sidebar. The <b>Account</b> tab shows your Email, Name, Company, and Position in a table. Use the search bar at the top to search account settings if needed. To change name, company, or position, click <b>Edit profile</b> and then <b>Save</b>.',
    tag: 'account',
  },
  {
    question: 'Where do I see my plan or billing information?',
    answer:
      'Go to <b>Your Account</b> from the sidebar. Open the <b>Plan & Billing</b> tab. You will see a table with Plan (current status) and Billing. Click <b>Go to Billing</b> to open the Billing page, where you can see subscription and billing details. Plan and billing features may show "Coming soon" while we roll them out.',
    tag: 'billing',
  },
  {
    question: 'How do I upgrade or manage my subscription?',
    answer:
      'Go to <b>Your Account</b> → <b>Plan & Billing</b> tab → click <b>Go to Billing</b>. The Billing & Subscription page shows available options (e.g. flexible plans, payment methods). If upgrade or payment options are not yet available, use the Support page: click <b>Create new request</b>, choose category <b>Billing</b>, and ask for plan changes or invoices. We typically respond within 2–3 business days.',
    tag: 'billing',
  },
  {
    question: 'How do I change my password?',
    answer:
      'Go to <b>Your Account</b> from the sidebar. Open the <b>Security</b> tab. Under <b>Password</b>, enter your current password, then your new password (min 8 characters, must include uppercase, number, and special character) and confirm it. Click <b>Change password</b>. After a successful change, you are logged out on all other devices but stay signed in on this device.',
    tag: 'account',
  },
  {
    question: 'How can I monitor different logins or active sessions?',
    answer:
      'Go to <b>Your Account</b> from the sidebar. Open the <b>Security</b> tab and scroll to <b>Active sessions</b>. You will see a table of devices where your account is signed in: Device, Location (IP), Last active, and Actions. The current device is marked <b>Current session</b>. To sign out another device, click <b>Revoke</b> for that row. If you are only on this device, the message will say so and sessions will appear after you sign in elsewhere.',
    tag: 'account',
  },
];

const CATEGORIES = [
  { value: 'Technical', label: 'Technical' },
  { value: 'Billing', label: 'Billing' },
  { value: 'Account', label: 'Account' },
  { value: 'Other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

function formatRelativeTime(dateString) {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  if (diffSeconds < 60) return 'less than a minute ago';
  if (diffMinutes < 60)
    return diffMinutes === 1 ? 'about 1 minute ago' : `about ${diffMinutes} minutes ago`;
  if (diffHours < 24)
    return diffHours === 1 ? 'about 1 hour ago' : `about ${diffHours} hours ago`;
  if (diffDays < 30) return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  if (diffMonths < 12) return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
  return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
}

function StatusBadge({ status }) {
  const map = {
    open: { label: 'Open', className: 'bg-blue-100 text-blue-700' },
    in_progress: { label: 'In progress', className: 'bg-amber-100 text-amber-700' },
    closed: { label: 'Closed', className: 'bg-gray-100 text-gray-700' },
  };
  const s = map[status] || map.open;
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${s.className}`}>
      {s.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const map = {
    low: { label: 'Low', className: 'bg-gray-100 text-gray-600' },
    medium: { label: 'Medium', className: 'bg-indigo-100 text-indigo-700' },
    high: { label: 'High', className: 'bg-red-100 text-red-700' },
  };
  const p = map[priority] || map.medium;
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${p.className}`}>
      {p.label}
    </span>
  );
}

const Help = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewRequest, setViewRequest] = useState(null);
  const [faqOpen, setFaqOpen] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    category: 'Other',
    priority: 'medium',
  });
  const [activeTab, setActiveTab] = useState('faq');
  const [searchTerm, setSearchTerm] = useState('');
  const tabsRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [openActionRequestId, setOpenActionRequestId] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const actionButtonRefs = useRef({});
  const [closeConfirmRequest, setCloseConfirmRequest] = useState(null);
  const [closingRequestId, setClosingRequestId] = useState(null);
  const [viewRequestLoading, setViewRequestLoading] = useState(false);
  const [viewRequestReply, setViewRequestReply] = useState('');
  const [viewRequestSubmitting, setViewRequestSubmitting] = useState(false);
  const viewRequestLoadedIdRef = useRef(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get('/api/help/requests');
      setRequests(res.data.requests || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load support requests');
      setRequests([]);
      toast.error('Failed to load support requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    if (!viewRequest?._id) {
      viewRequestLoadedIdRef.current = null;
      return;
    }
    if (viewRequestLoadedIdRef.current === viewRequest._id) return;
    viewRequestLoadedIdRef.current = viewRequest._id;
    setViewRequestLoading(true);
    axios
      .get(`/api/help/requests/${viewRequest._id}`)
      .then((res) => {
        setViewRequest(res.data.request);
      })
      .catch(() => {
        toast.error('Failed to load request details');
        setViewRequest(null);
      })
      .finally(() => setViewRequestLoading(false));
  }, [viewRequest?._id]);

  const activeRequests = requests.filter((r) => r.status !== 'closed');
  const closedRequests = requests.filter((r) => r.status === 'closed');

  const tabsList = [
    { id: 'faq', label: 'Frequently Asked Questions', count: FAQ_ITEMS.length },
    { id: 'active', label: 'Active requests', count: activeRequests.length },
    { id: 'closed', label: 'Closed requests', count: closedRequests.length },
  ];

  useLayoutEffect(() => {
    const container = tabsRef.current;
    if (!container) return;
    const tabEl = container.querySelector(`[data-tab-id="${activeTab}"]`);
    if (!tabEl) return;
    const measure = () => {
      const left = tabEl.offsetLeft;
      const width = tabEl.offsetWidth;
      setIndicator((prev) => (prev.left === left && prev.width === width ? prev : { left, width }));
    };
    measure();
    const t1 = setTimeout(measure, 0);
    const t2 = setTimeout(measure, 50);
    const rafId = requestAnimationFrame(() => {
      measure();
      requestAnimationFrame(measure);
    });
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [activeTab]);

  const creatorName = (req) => {
    const u = req.createdBy;
    if (!u) return '—';
    return u.name || u.email || '—';
  };

  const authorName = (author) => {
    if (!author) return '—';
    return author.name || author.email || '—';
  };

  const formatDateTime = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  const submitViewRequestReply = async () => {
    const text = (viewRequestReply || '').trim();
    if (!text || !viewRequest?._id) return;
    setViewRequestSubmitting(true);
    try {
      const res = await axios.post(`/api/help/requests/${viewRequest._id}/comments`, { text });
      setViewRequest(res.data.request);
      setViewRequestReply('');
      toast.success('Reply sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setViewRequestSubmitting(false);
    }
  };

  const filteredRequests =
    activeTab === 'active'
      ? activeRequests.filter((req) => {
          const creatorStr = creatorName(req).toLowerCase();
          const title = (req.title || '').toLowerCase();
          const category = (req.category || '').toLowerCase();
          const term = searchTerm.toLowerCase().trim();
          if (!term) return true;
          return (
            title.includes(term) || category.includes(term) || creatorStr.includes(term)
          );
        })
      : activeTab === 'closed'
        ? closedRequests.filter((req) => {
            const creatorStr = creatorName(req).toLowerCase();
            const title = (req.title || '').toLowerCase();
            const category = (req.category || '').toLowerCase();
            const term = searchTerm.toLowerCase().trim();
            if (!term) return true;
            return (
              title.includes(term) || category.includes(term) || creatorStr.includes(term)
            );
          })
        : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedButton = Object.values(actionButtonRefs.current).some(
        (ref) => ref && ref.contains && ref.contains(event.target)
      );
      const clickedMenu = event.target.closest('[data-action-menu]');
      if (!clickedButton && !clickedMenu && openActionRequestId) {
        setOpenActionRequestId(null);
        setActionMenuPosition(null);
      }
    };
    if (openActionRequestId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openActionRequestId]);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!createForm.title.trim()) {
      toast.error('Please enter a request title');
      return;
    }
    if (!createForm.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    try {
      setSubmitting(true);
      await axios.post('/api/help/requests', {
        title: createForm.title.trim(),
        description: createForm.description.trim(),
        category: createForm.category,
        priority: createForm.priority,
      });
      toast.success('Support request created. We will get back to you soon.');
      setShowCreateModal(false);
      setCreateForm({ title: '', description: '', category: 'Other', priority: 'medium' });
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseRequest = async (req) => {
    try {
      setClosingRequestId(req._id);
      await axios.patch(`/api/help/requests/${req._id}`, { status: 'closed' });
      toast.success('Request closed');
      setCloseConfirmRequest(null);
      setOpenActionRequestId(null);
      setActionMenuPosition(null);
      if (viewRequest && viewRequest._id === req._id) setViewRequest(null);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to close request');
    } finally {
      setClosingRequestId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-6 pb-12">
        {/* Header - match ResultsAnalytics */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Help & Support
            </h1>
            <p className="mt-1.5 text-gray-600 text-sm sm:text-base">
              FAQs, support requests, and get help from our team.
            </p>
            {/* <p className="mt-1 text-gray-500 text-xs sm:text-sm">
              Typical response time is 2–3 business days.
            </p> */}
          </div>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="px-5 py-2.5 bg-transparent border-2 border-gray-900 text-gray-900 rounded-lg hover:bg-[#007BFF] hover:border-[#007BFF] hover:text-white flex items-center gap-2 font-semibold transition-all duration-200 text-sm shrink-0"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Create new request</span>
          </button>
        </div>

        {/* Tabs - FAQs, Active, Closed with sliding underline */}
        <div className="mb-6">
          <div ref={tabsRef} className="relative flex items-center gap-8 min-w-0">
            {tabsList.map((tab) => (
              <button
                key={tab.id}
                type="button"
                data-tab-id={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative z-10 pb-3 pt-0.5 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  activeTab === tab.id ? 'text-[#242124]' : 'text-[#242124] hover:text-[#242124]'
                }`}
              >
                {tab.label}
                <span
                  className={`ml-2 py-0.5 px-2 rounded-full text-xs font-semibold ${
                    activeTab === tab.id
                      ? 'bg-[#8472B3]/10 text-[#8472B3]'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
            <motion.div
              className="absolute bottom-0 left-0 h-0.5 bg-[#8472B3] z-0"
              initial={false}
              animate={{
                left: indicator.left,
                width:
                  indicator.width > 0
                    ? indicator.width
                    : activeTab === 'faq'
                      ? 60
                      : activeTab === 'active'
                        ? 80
                        : 90,
              }}
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              style={{ willChange: 'left, width' }}
            />
          </div>
        </div>

        {/* Search bar - only for Active / Closed */}
        {(activeTab === 'active' || activeTab === 'closed') && (
          <div className="mb-0">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search requests by title, category, or created by..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-12 py-3 bg-white border-0 rounded-lg shadow-sm text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-colors duration-200 ease-out ${
                  searchTerm ? 'pr-24' : 'pr-4'
                }`}
              />
              {searchTerm && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium tabular-nums">
                  {filteredRequests.length} result{filteredRequests.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Content by tab */}
        {activeTab === 'faq' && (
          <>
            <div className="px-6 py-4 bg-[#F7F9FC] mb-0">
              <h2 className="text-base font-semibold text-[#242124]">Frequently Asked Questions</h2>
            </div>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200/80">
              <div className="divide-y divide-gray-100">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div key={idx}>
                    <button
                      type="button"
                      onClick={() => setFaqOpen(faqOpen === idx ? null : idx)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50/50 transition"
                    >
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{faq.question}</span>
                        {faq.tag && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              faq.tag === 'technical'
                                ? 'bg-blue-100 text-blue-800'
                                : faq.tag === 'account'
                                  ? 'bg-green-100 text-green-800'
                                  : faq.tag === 'billing'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {faq.tag.charAt(0).toUpperCase() + faq.tag.slice(1)}
                          </span>
                        )}
                      </span>
                      {/* {faqOpen === idx ? (
                        <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0 ml-2" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0 ml-2" />
                      )} */}
                    </button>
                    {faqOpen === idx && (
                      <div
                        className="px-6 pb-4 pt-0 text-sm text-gray-600 bg-gray-50/50"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {(activeTab === 'active' || activeTab === 'closed') && (
          <>
            <div className="px-6 py-4 bg-[#F7F9FC] mb-0">
              <h2 className="text-base font-semibold text-[#242124]">
                {activeTab === 'active' ? 'Active requests' : 'Closed requests'}
              </h2>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-[#F7F9FC] rounded-xl shadow-sm overflow-hidden"
            >
              {loading ? (
                <div className="text-center py-16 px-6">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">Loading requests...</p>
                </div>
              ) : error ? (
                <div className="text-center py-16 px-6">
                  <p className="text-gray-600">{error}</p>
                </div>
              ) : filteredRequests.length === 0 ? (
                <div className="text-center py-16 px-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                    <HelpCircle className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {requests.length === 0
                      ? activeTab === 'active'
                        ? 'No support requests yet'
                        : 'No closed requests yet'
                      : 'No requests match your search'}
                  </h3>
                  <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                    {activeTab === 'active' && requests.length === 0
                      ? 'Create a request when you need help from our team.'
                      : 'Try adjusting your search or switching tabs.'}
                  </p>
                  {activeTab === 'active' && requests.length === 0 && (
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(true)}
                      className="px-5 py-2.5 bg-[#003153] text-white rounded-lg hover:bg-[#004175] flex items-center gap-2 font-semibold shadow-md mx-auto text-sm"
                    >
                      <PlusIcon className="h-5 w-5" />
                      Create new request
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#007BFF]">
                        <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider rounded-tl-xl">
                          Request ID
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                          Request name
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden sm:table-cell">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                          Priority
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                          Category
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                          Created by
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                          Created date
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                          Last updated
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider w-14 rounded-tr-xl">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((req) => (
                        <tr
                          key={req._id}
                          className={`group transition-colors bg-white border-b border-l border-r border-gray-300/50 ${
                            activeTab === 'active'
                              ? 'cursor-pointer hover:bg-[#8472B3]/10'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={
                            activeTab === 'active'
                              ? () => setViewRequest(req)
                              : () => setViewRequest(req)
                          }
                        >
                          <td className="px-6 py-4">
                            <div className="text-sm font-semibold text-[#242124]">
                              {req.requestId || '—'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-semibold text-[#242124] line-clamp-2">
                              {req.title}
                            </div>
                            <div className="md:hidden text-xs text-[#242124] mt-0.5">
                              {creatorName(req)} · {formatDate(req.createdAt)}
                            </div>
                          </td>
                          <td className="px-6 py-4 hidden sm:table-cell">
                            <StatusBadge status={req.status} />
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <PriorityBadge priority={req.priority} />
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <span className="text-sm text-[#242124]">{req.category || '—'}</span>
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <span className="text-sm text-[#242124]">{creatorName(req)}</span>
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <span className="text-sm text-[#242124]">
                              {formatDate(req.createdAt)}
                            </span>
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <span className="text-sm text-[#242124]">
                              {formatRelativeTime(req.updatedAt)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right relative">
                            <div
                              className="relative action-menu-container"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                ref={(el) => (actionButtonRefs.current[req._id] = el)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openActionRequestId === req._id) {
                                    setOpenActionRequestId(null);
                                    setActionMenuPosition(null);
                                  } else {
                                    const button = actionButtonRefs.current[req._id];
                                    if (button) {
                                      const rect = button.getBoundingClientRect();
                                      setActionMenuPosition({
                                        top: rect.top,
                                        right: window.innerWidth - rect.right,
                                      });
                                    }
                                    setOpenActionRequestId(req._id);
                                  }
                                }}
                                className="text-gray-400 hover:text-gray-700 inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 transition-colors"
                                title="Actions"
                              >
                                <EllipsisVerticalIcon className="h-5 w-5" />
                              </button>
                              {openActionRequestId === req._id &&
                                actionMenuPosition &&
                                createPortal(
                                  <div
                                    data-action-menu
                                    className="fixed min-w-[180px] bg-white rounded-xl shadow-lg border border-gray-100 z-[9999] overflow-hidden"
                                    style={{
                                      top: `${actionMenuPosition.top}px`,
                                      right: `${actionMenuPosition.right}px`,
                                      transform: 'translateY(calc(-100% - 8px))',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setViewRequest(req);
                                        setOpenActionRequestId(null);
                                        setActionMenuPosition(null);
                                      }}
                                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                                    >
                                      <EyeIcon className="h-4 w-4 text-gray-400 shrink-0" />
                                      <span>View details</span>
                                    </button>
                                    {activeTab === 'active' && req.status !== 'closed' && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCloseConfirmRequest(req);
                                          setOpenActionRequestId(null);
                                          setActionMenuPosition(null);
                                        }}
                                        className="w-full text-left px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 flex items-center gap-3 transition-colors border-t border-gray-100"
                                      >
                                        <XMarkIcon className="h-4 w-4 text-amber-500 shrink-0" />
                                        <span>Close request</span>
                                      </button>
                                    )}
                                  </div>,
                                  document.body
                                )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          </>
        )}
      </div>

      {/* Create request modal - match Screening Details modal: height, width, alignment, tip box, colors */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity"
              onClick={() => !submitting && setShowCreateModal(false)}
              aria-hidden="true"
            />
            <div className="relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-xl overflow-hidden">
              <form onSubmit={handleCreateSubmit} className="flex flex-col min-h-0">
                {/* Header: close X top right */}
                <div className="flex justify-end pt-5 pr-6 pb-0 pl-6 shrink-0">
                  <button
                    type="button"
                    onClick={() => !submitting && setShowCreateModal(false)}
                    className="rounded-lg p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                    aria-label="Close"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto px-6 pb-6">
                  <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                    Create new request
                  </h2>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                    Submit a support request and our team will get back to you within 2-3 business days. Choose a clear title
                    and category so we can help you faster.
                  </p>
                  {/* Tip box - light blue, blue border and text */}
                  {/* <div className="mt-5 p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <p className="text-sm">
                      <span className="font-semibold text-blue-600">Tip:</span>
                      <span className="ml-1 text-blue-700">
                        Use a descriptive title (e.g. &quot;Billing question about invoice #123&quot; or
                        &quot;Cannot invite candidates to assessment&quot;) and pick the category that best
                        fits. Our team typically responds within 2–3 business days.
                      </span>
                    </p>
                  </div> */}
                  {/* Form fields */}
                  <div className="mt-6 space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Request name *
                      </label>
                      <input
                        type="text"
                        value={createForm.title}
                        onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
                        placeholder="e.g. Billing question, Technical issue with screenings"
                        required
                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={createForm.description}
                        onChange={(e) =>
                          setCreateForm((f) => ({ ...f, description: e.target.value }))
                        }
                        placeholder="e.g. I cannot upload my candidate list—the 'Continue' button stays on 'Processing...' for over 10 minutes. Job: Sales Manager. Browser: Chrome."
                        rows={4}
                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-colors resize-y min-h-[100px]"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Category
                        </label>
                        <select
                          value={createForm.category}
                          onChange={(e) =>
                            setCreateForm((f) => ({ ...f, category: e.target.value }))
                          }
                          className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-colors"
                        >
                          {CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Priority
                        </label>
                        <select
                          value={createForm.priority}
                          onChange={(e) =>
                            setCreateForm((f) => ({ ...f, priority: e.target.value }))
                          }
                          className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-colors"
                        >
                          {PRIORITIES.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Footer: primary button bottom right */}
                <div className="shrink-0 border-t border-gray-200 bg-gray-50/80 px-6 py-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    disabled={submitting}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors shadow-sm"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit request'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Close request confirmation modal */}
      {closeConfirmRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-gray-900/50 transition-opacity"
              onClick={() => !closingRequestId && setCloseConfirmRequest(null)}
              aria-hidden="true"
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900">Close support request?</h3>
              <p className="mt-2 text-sm text-gray-600">
                This request will be moved to Closed requests. You can still view it there.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setCloseConfirmRequest(null)}
                  disabled={!!closingRequestId}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleCloseRequest(closeConfirmRequest)}
                  disabled={!!closingRequestId}
                  className="px-4 py-2.5 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {closingRequestId === closeConfirmRequest._id ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Closing...
                    </>
                  ) : (
                    'Close request'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View request detail modal - same structure as Create new request modal */}
      {viewRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <div
              className="fixed inset-0 bg-gray-900/50 transition-opacity"
              onClick={() => setViewRequest(null)}
              aria-hidden="true"
            />
            <div className="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-xl overflow-hidden">
              {/* Header: close X top right - same as Create modal */}
              <div className="flex justify-end pt-5 pr-6 pb-0 pl-6 shrink-0">
                <button
                  type="button"
                  onClick={() => setViewRequest(null)}
                  className="rounded-lg p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              {/* Scrollable content - same padding as Create modal */}
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                  Request details
                </h2>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                  View your support request and conversation with our team.
                </p>

                {viewRequestLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="mt-6 space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Request ID
                      </label>
                      <p className="text-gray-900">{viewRequest.requestId || '—'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Title
                      </label>
                      <p className="text-gray-900">{viewRequest.title}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Description
                      </label>
                      <p className="text-gray-900 whitespace-pre-wrap">
                        {viewRequest.description || '—'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Opened by
                      </label>
                      <p className="text-gray-900">
                        {creatorName(viewRequest)} on {formatDateTime(viewRequest.createdAt)}
                      </p>
                    </div>
                    {(viewRequest.inProgressAt || viewRequest.closedAt) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Timeline
                        </label>
                        <div className="text-sm text-gray-700 space-y-1">
                          {viewRequest.inProgressAt && (
                            <p>
                              Set to In progress by{' '}
                              {viewRequest.inProgressBy
                                ? authorName(viewRequest.inProgressBy)
                                : 'Support'}{' '}
                              on {formatDateTime(viewRequest.inProgressAt)}
                            </p>
                          )}
                          {viewRequest.closedAt && (
                            <p>
                              Closed on {formatDateTime(viewRequest.closedAt)}
                              {viewRequest.resolvedBy &&
                                ` by ${authorName(viewRequest.resolvedBy)}`}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Category
                        </label>
                        <p className="text-gray-900">{viewRequest.category || '—'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Priority
                        </label>
                        <div className="mt-0.5">
                          <PriorityBadge priority={viewRequest.priority} />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Status
                      </label>
                      <div className="mt-0.5">
                        <StatusBadge status={viewRequest.status} />
                      </div>
                    </div>

                    {viewRequest.comments && viewRequest.comments.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Comments
                        </label>
                        <div className="space-y-3 mt-1">
                          {viewRequest.comments.map((c) => {
                            const isCurrentUser =
                              viewRequest.createdBy &&
                              c.author &&
                              String(viewRequest.createdBy._id) === String(c.author._id);
                            return (
                              <div
                                key={c._id || c.createdAt}
                                className={`rounded-lg p-3 max-w-[85%] ${
                                  isCurrentUser
                                    ? 'ml-auto bg-blue-50 border border-blue-200'
                                    : 'mr-auto bg-gray-50 border border-gray-200'
                                }`}
                              >
                                <p className="text-xs font-medium text-gray-600 mb-1">
                                  {isCurrentUser ? 'You' : authorName(c.author)} ·{' '}
                                  {formatDateTime(c.createdAt)}
                                </p>
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                  {c.text}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {viewRequest.status !== 'closed' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Add reply
                        </label>
                        <textarea
                          value={viewRequestReply}
                          onChange={(e) => setViewRequestReply(e.target.value)}
                          placeholder="Type your reply..."
                          rows={4}
                          className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-colors resize-y min-h-[100px]"
                        />
                      </div>
                    )}

                    {viewRequest.status === 'closed' && (
                      <p className="text-sm text-gray-600">
                        This request is closed. You can open a new request if you need more help.
                      </p>
                    )}
                  </div>
                )}
              </div>
              {/* Footer - same as Create modal: border-t, bg-gray-50/80, justify-end gap-3 */}
              {!viewRequestLoading && viewRequest && (
                <div className="shrink-0 border-t border-gray-200 bg-gray-50/80 px-6 py-4 flex justify-end gap-3">
                  {viewRequest.status !== 'closed' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setCloseConfirmRequest(viewRequest);
                          setViewRequest(null);
                        }}
                        className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      >
                        Close request
                      </button>
                      <button
                        type="button"
                        onClick={submitViewRequestReply}
                        disabled={viewRequestSubmitting || !viewRequestReply.trim()}
                        className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors shadow-sm"
                      >
                        {viewRequestSubmitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Send reply'
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setViewRequest(null)}
                      className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                      Done
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Help;
